import type { FormField, FormFields } from '~~/types'
import { validEmail } from './use-functions'

export default ({ id, sections }) => {
  const mathItUp = {
    '===': (x, y) => x === y,
    '!==': (x, y) => x !== y,
    '>': (x, y) => x > y,
    '<': (x, y) => x < y,
    '>=': (x, y) => x >= y,
    '<=': (x, y) => x <= y,
  } as const

  const formEl = ref()
  const errorMessage = ref('')
  const allFields = sections.reduce(
    (res, section) =>
      res.concat(section.fields.filter((field) => field.type !== 'element')),
    []
  )
  const fieldIsReady = (field: FormField) =>
    !field.required ||
    !conditional(field) ||
    (form[field.name] &&
      (field.type === 'email' ? validEmail(form[field.name]) : true))

  function conditional(field: FormField) {
    if (!field.condition) return true

    const [fieldName, expression, m] = field.condition.split(' ')
    const target = form[fieldName]
    let match: string | number | boolean | string[] = ''
    if (m.startsWith('"')) match = m.replace(/"/g, '')
    else match = form[m]
    if (!(fieldName in form))
      return console.error(
        `Cannot find ${fieldName} in expression '${field.condition}'`
      )
    if (!match)
      return console.error(
        `Cannot find ${m} in expression '${field.condition}'`
      )

    return mathItUp[expression as keyof typeof mathItUp](target, match)
  }

  const form = reactive<FormFields>({
    ...allFields?.reduce(
      (details, field) => {
        if (!field.name) throw new Error(`Missing field name: ${field.label}.`)
        const value = field.initialValue as any
        details[field.name] =
          field.type === 'checkbox' || field.type === 'giftaid'
            ? value || field.options?.length
              ? []
              : 'no'
            : field.type === 'select'
            ? value || []
            : process.env.NODE_ENV === 'development'
            ? field.name === 'organisation'
              ? 'Concision'
              : field.name === 'address'
              ? '57 Heaviside Place'
              : field.name === 'postcode'
              ? 'DH1 1JG'
              : value || ''
            : value || ''
        return details
      },
      { type: id } as FormFields
    ),
  })

  const giftaidWarning = computed(() => form.giftaid && form.giftaid === 'no')

  const stripeEnabled = computed(() =>
    allFields.some((f) => f.type === 'stripe')
  )

  const submission = computed(() => ({
    name: `${form.forename} ${form.surname}`,
    email: String(form.email),
    amount: Number(form.amount),
    currency: 'gbp',
  }))

  const formIsReady = (cardComplete: Ref<boolean>) => {
    const fieldsNotReady = ref([])

    allFields.forEach((field: FormField) =>
      fieldIsReady(field) || field.type === 'stripe'
        ? cardComplete.value
        : fieldsNotReady.value.push(field.label)
    )

    return fieldsNotReady.value
  }

  return {
    formEl,
    errorMessage,
    giftaidWarning,
    allFields,
    stripeEnabled,
    form,
    conditional,
    fieldIsReady,
    formIsReady,
    submission,
  }
}
